import ReactDOM from 'react-dom'
import { useEffect, useRef } from 'react'
import { Canvas, useFrame, extend, useThree } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import { SimpleReverb } from './SimpleReverb'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { AfterimagePass } from 'three/examples/jsm/postprocessing/AfterimagePass'
import './styles.css'

let randI = (i) => Math.floor(i * fxrand())
let rand = (arr) => arr[randI(arr.length)]
let bg = "white"
let colDec = randI(100) * 0.01
let peq = 0.01
var getFrequency = function (_) { var e, r; return e = 3 === _.length ? _.charAt(2) : _.charAt(1), 440 * Math.pow(2, ((r = (r = ["A", "A#", "B", "C", "C#", "D", "D#", "E", "F", "F#", "G", "G#"].indexOf(_.slice(0, -1))) < 3 ? r + 12 + (e - 1) * 12 + 1 : r + (e - 1) * 12 + 1) - 49) / 12) };
let getBpm = () => (12 + randI(21)) * 5
let getFreq = () => getFrequency(rand(['C', 'D', 'E', 'F', 'G', 'A']) + rand([2, 3, 4, 5, 6, 7, 8])) * rand([1, 1, 1, 2, 3])

let sqrrrt = 9 + (randI(90) * 0.05)
let detail = 2 + randI(8)
let colMult = 1 + (randI(1000) * 0.001)
let toType = rand([0, 0, 0, 0, 0, 0, 0, 0, 1])
let bitCheck = rand([2, 2, 2, 3, 4,5,6,7,8])
let smoothing = 1 + (randI(500) * 0.01)
let startTime = 1 + randI(6)
let tri = rand(['sin', 'cos'])
let triSize = randI(40)*0.1
let triSize2 = 8+randI(7)
let colSpread = 0.1

class AfterBurn extends AfterimagePass {
  constructor() {
    super(1)
    this.shaderMaterial.uniforms.time = { value: startTime }
    this.shaderMaterial.uniforms.colMult = { value: colMult }
    this.shaderMaterial.uniforms.colDec = { value: colDec }
    this.shaderMaterial.uniforms.peq = { value: peq }
    this.shaderMaterial.uniforms.sqrrrt = { value: sqrrrt }
    this.shaderMaterial.uniforms.detail = { value: detail }
    this.shaderMaterial.uniforms.toType = { value: toType }
    this.shaderMaterial.uniforms.bitCheck = { value: bitCheck }
    this.shaderMaterial.uniforms.smoothing = { value: smoothing }
    this.shaderMaterial.uniforms.triSize = { value: triSize }
    this.shaderMaterial.uniforms.triSize2 = { value: triSize2 }
    this.shaderMaterial.onBeforeCompile = (shader) => {
      shader.fragmentShader =
        `
uniform float time;
uniform float colDec;
uniform float peq;
uniform float sqrrrt;
uniform float colMult;
uniform float smoothing;
uniform float triSize;
uniform float triSize2;
uniform int detail;
uniform int toType;
uniform uint bitCheck;
varying vec2 vUv;

uint ihash(uint x) {
  x += 0x12345678u;
    x ^= x >> 16;
    x *= 0x7feb352dU;
    x ^= x >> 15;
    x *= 0x846ca68bU;
    x ^= x >> 12;
    return x;
}

vec3 h2rgb(float h) {
  vec3 rgb = clamp( abs(mod(h*22.0+vec3(0.0,4.0,3.0),colMult)-2.0)-1.0, 0.0, 1.0 );
  return rgb*rgb*(4.0-smoothing*rgb); // cubic smoothing	
}

float rectdist(vec2 p, vec2 a, vec2 b) {
  return max(max(a.y-p.y,p.y-b.y),
             max(a.x-p.x,p.x-b.x));
}
vec4 when_gt( vec4 x, float y ) {
    return max( sign( x - y ), 0.000000001 );
}

void main() {
  float phi = peq*(sqrt(sqrrrt)-0.5); 
  vec2 p = (-1.0 + 0.5 *vUv);
  float px = fwidth(p.x);
  vec3 col = vec3(0,0,1);
  vec2 a = vec2(-1.0-phi,-1);
  vec2 b = vec2(1.0+phi,1);
  int type = 0;
  uint h = uint(time*0.0);
  float d = rectdist(p,a,b);
  if (d < 0.0) {
    int N = detail;
    for (int i = 0; 
         i < N; 
         i++, h = ihash(h^uint(type))) {
      if (type == 0) {
        bool flip = bool(h & bitCheck);
        if (flip) { 
          p = p.yx; 
          a = a.yx; 
          b = b.yx; 
        }
          float x1 = (1.0-phi)*a.x + phi*b.x*(${tri}(time*0.5*p.y-p.x*triSize2)*p.x*triSize);
          if (p.x < x1) { 
            b = vec2(x1,b.x); 
            type = 1; 
          }
          else { 
            a = vec2(x1,a.y); 
            type = 2; 
          }
        if (flip) { 
          p = p.yx; 
          a = a.yx; 
          b = b.yx; 
        }
      } else {
        bool flip = b.y-a.y > b.x-a.x;
        if (flip) { 
          p = p.yx; 
          a = a.yx; 
          b = b.yx; 
        }
          float x1 = a.x+b.y-a.y;
          if (p.x < x1) { 
            b = vec2(x1,b.y); 
            type = 0; 
          }
          else { 
            a = vec2(x1,a.y); 
            type = toType; 
          }
        if (flip) { 
          p = p.yx; 
          a = a.yx; 
          b = b.yx; 
        }
      }
    }
    col = h2rgb(colDec+(float(h)*${colSpread})/exp2(32.0));
  }
  gl_FragColor = vec4(col,0.01);
}

`
    }
  }
}
extend({ EffectComposer, RenderPass, AfterBurn })
let pi = 0
const Tint = () => {
  const ref = useRef()
  const { size, gl, scene, camera } = useThree()
  const composer = useRef()
  useFrame(() => {
    ref.current.uniforms.peq.value = pTotal
    composer.current.render()
  }, 1)
  return (
    <effectComposer ref={composer} args={[gl]}>
      <renderPass attachFns={['addPass']} args={[scene, camera]} />
      <afterBurn attachFns={['addPass']} args={[size]} ref={ref} />
    </effectComposer>
  )
}

let seqs = []
function App() {
  const editor = useRef();
  const start = () => {
    if (!audioStarted) {
      play()
      noises.forEach((n,ni) => {
        n.start(0)
      });
      audioStarted = true;
    }
  }
  const update = () => {
    innerHTML = editor.current.innerText.length ? editor.current.innerText : '.'
    seqs = []
    innerHTML.split('\n').map((v, vi) => {
      if (v.length) {
        if (!noises[vi]) {
          createRow(true)
        }
        seqs.push(v)
      }
    })
    setFontSize()
  }
  const paste = (e) => {
    e.preventDefault();
    document.execCommand('inserttext', false, e.clipboardData.getData('text/plain'));
  }
  useEffect(update, [])
  return (
    <div id="main" onClick={() => start()}
      onDoubleClick={() => {
        toggleFullScreen()
      }}
    >
      <Canvas flat={false} linear={false} dpr={[1, 2]} camera={{ position: [0, 0, 2], near: 0.0001, far: 1, fov: 35 }}
        gl={{ alpha: false, antialias: false, stencil: false, depth: false }}
      >
        <color attach="background" args={[bg]} />
        <OrbitControls />
        <Tint />
      </Canvas>
      <div id="wrapper">
        <div id="inner">
          <div id="editor" ref={editor} contentEditable
            onInput={update}
            onPaste={paste}
            spellCheck="false"
            autoCapitalize="none"
            dangerouslySetInnerHTML={{__html: innerHTML}}
            >
            
          </div>
          <div id="fakeEditor" />
        </div>
      </div>
    </div>
  )
}


let patterns = [
  'x x',
  ' x ',
  'x x ',
  'xx x x',
  'x',
  'x x',
  ' x ',
  'x x ',
  'xx x x',
  'x',
  'x x',
  ' x ',
  'x x ',
  'xx x x',
  'x',
  'x x',
  ' x ',
  'x x ',
  'xx x x',
  'x',
  '&nbsp;&nbsp;x',
  '&nbsp;&nbsp;x x',
  '&nbsp;',
]
let innerHTML = ''
let letters = 'abcdefghijklmnopqrstuvwyzabcdefghijklmnopqrstuvwyzåäöåäöåäöåäö0123456789'

let letter = ''
let rowAmt = 1+randI(rand([1,2,2,2,3,4,5]))
new Array(rowAmt).fill().map(() => {
  letter = rand(letters)
  innerHTML += '<div>'
  new Array(1+randI(6)).fill().map(() => {
    if(randI(10)>6){
      letter = rand(letters)
    }
    if(randI(50)>48){
      innerHTML += ' '+rand(['<3', ':>', 'hi',':-)',':)',');','(:','<3', ':>', 'hi',':-)',':)',');','(:','hej','hello'])+' '
    }
    
    innerHTML += rand(patterns).replace(/x/g, letter);
  })
  innerHTML += '</div>'
})


window.AudioContext = window.AudioContext || window.webkitAudioContext;
let audioStarted = false;
let audioContext = new AudioContext();
let bufferSize = 2 * audioContext.sampleRate,
  noiseBuffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate),
  output = noiseBuffer.getChannelData(0);
let b0, b1, b2, b3, b4, b5, b6;
b0 = b1 = b2 = b3 = b4 = b5 = b6 = 0.0;
for (let i = 0; i < bufferSize; i++) {
  const white = fxrand() * 2 - 1;
  b0 = 0.99886 * b0 + white * 0.0555179;
  b1 = 0.99332 * b1 + white * 0.0750759;
  b2 = 0.96900 * b2 + white * 0.1538520;
  b3 = 0.86650 * b3 + white * 0.3104856;
  b4 = 0.55000 * b4 + white * 0.5329522;
  b5 = -0.7616 * b5 - white * 0.0168980;
  output[i] = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362;
  output[i] *= 0.13;
  b6 = white * 0.115926;
}

let filters = [];
let fgains = [];
let panners = [];
let noises = [];

let NUM = 5
let charFreqs = new Array(500).fill().map(() => new Array(NUM).fill().map(() => getFreq()))
let charRelease = new Array(500).fill().map(() => randI(15) * 0.001)
let charAttack = new Array(500).fill().map(() => randI(80) * 0.002)

let r = SimpleReverb(audioContext);
r.connect(audioContext.destination);
r.time = 4
r.filterType = 'lowpass'
r.cutoff.value = 300
r.wet.value = 0.4
r.dry.value = 0.2

function createRow(start) {
  let n = audioContext.createBufferSource();
  n.buffer = noiseBuffer;
  n.loop = true;
  noises.push(n)
  if (start) {
    n.start()
  }

  panners.push([])
  filters.push([])
  fgains.push([])
  let l = fgains.length - 1;

  for (let i = 0; i < NUM; i++) {
    let p = audioContext.createStereoPanner();
    panners[l].push(p)

    let f = audioContext.createBiquadFilter();
    f.type = "bandpass";
    f.Q.value = 300;
    f.frequency.value = getFreq()
    filters[l].push(f);

    let g = audioContext.createGain();
    g.gain.value = 0;
    fgains[l].push(g);

    n.connect(f);
    f.connect(g);
    g.connect(p);
    p.connect(r);
  }
}

new Array(rowAmt).fill().map(() => {
  createRow()
})


ReactDOM.render(
  <App />,
  document.getElementById('root')
)


function toggleFullScreen() {
  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen();
  }
}
let longestRow = 0
function setFontSize() {
  longestRow = 1
  innerHTML.split('\n').map((v) => {
    longestRow = Math.max(longestRow, v.length)
  })
  let ratio = window.innerWidth / window.innerHeight
  let size = Math.max(1,Math.min(((140 - (seqs.length * 8 * ratio)) / ((longestRow * 0.8) + ((seqs.length * 0.8) / ratio))), 40))
  document.getElementById('fakeEditor').style.fontSize = document.getElementById('editor').style.fontSize = size + 'vw'
  document.getElementById('fakeEditor').style.bottom = -(size*0.1)+'vw'
}
setFontSize()

let c = 0
let pTotal = 0.05
let beat = () => {
  pTotal = 0
  c++
  pi = (pi + 1) % longestRow
  seqs.forEach((seq, si) => {
    let charCode = seq.charCodeAt(pi)
    if (seq.length <= pi ||
      charCode === 32 ||
      charCode === 160 ||
      !charCode ||
      typeof (seq[pi]) === 'undefined') {
      return
    }
    panners[si].forEach((p, i) => {
      let pa = ((charCode * 0.02) % 1.8) - 0.9
      p.pan.value = i % 2 ? pa : -pa
    })
    charFreqs[charCode].forEach((f, fi) => {
      filters[si][fi].frequency.value = f
    })

    fgains[si].forEach((g, gi) => {
      g.gain.linearRampToValueAtTime(1, audioContext.currentTime + charAttack[charCode])
      g.gain.linearRampToValueAtTime(0, audioContext.currentTime + charAttack[charCode] + charRelease[charCode])
    })
    pTotal += (seq.charCodeAt(pi) * 0.001)
  })

  let st = ''
  new Array(longestRow).fill().map((_, i) => { st += i === pi ? '_' : '&nbsp;' })
  document.getElementById('fakeEditor').innerHTML = st
  setFontSize()
}
function scheduleNote(beatNumber, time) {
  notesInQueue.push({ note: beatNumber, time: time });
  beat()
}
function scheduler() {
  while (nextNoteTime < audioContext.currentTime + scheduleAheadTime) {
    scheduleNote(current16thNote, nextNoteTime);
    nextNote();
  }
}

function nextNote() {
  var secondsPerBeat = 60.0 / bpm;
  nextNoteTime += 0.25 * secondsPerBeat;
  current16thNote++;
  if (current16thNote == 16) {
    current16thNote = 0;
  }
}

var isPlaying = false;
var current16thNote;
var bpm = getBpm()
var lookahead = 1.0;
var scheduleAheadTime = 0.1;
var nextNoteTime = 0.0;
var notesInQueue = [];
var timerWorker = null;
function play() {
  isPlaying = true;
  if (isPlaying) {
    current16thNote = 0;
    nextNoteTime = audioContext.currentTime;
    timerWorker.postMessage("start");
    return "stop";
  } else {
    timerWorker.postMessage("stop");
    return "play";
  }
}

function init() {
  timerWorker = new Worker("./metronomeworker.js");
  timerWorker.onmessage = function (e) {
    if (e.data == "tick") {
      scheduler();
    }
    else
      console.log("message: " + e.data);
  };
  timerWorker.postMessage({ "interval": lookahead });
}

const feats = window.$fxhashFeatures = {bpm}
window.addEventListener("load", init);
console.log('%c ⌨️⌨️ @ramioso 2023-03-04 ⌨️⌨️', 'color: hotpink');
